import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Doc, Id } from "@cvx/_generated/dataModel";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filter, Loader2, Mail, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CampaignSelector } from "./CampaignSelector";
import { Route as CreatorProfilePage } from "@/routes/_app/_auth/creators/$creatorId";
import { CreatorsList } from "./CreatorsList";
// Extracted components
import { InviteCreatorDialog } from "./InviteCreatorDialog";
import StatusBadge from "../Generic/StatusBadge";
import { api } from "@cvx/_generated/api";
import { formatDate } from "@/utils/format";
import { useAccount } from "@/state/useAccount";
import { useNavigate } from "@tanstack/react-router";
import { usePaginatedQuery } from "convex/react";
import { useToast } from "@/components/ui/use-toast";

// Helper function to render markdown text
const renderMarkdown = (text: string) => {
  // Convert markdown to HTML-like JSX
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let currentList: React.ReactNode[] = [];
  let inList = false;

  const processLine = (line: string, index: number) => {
    // Handle bullet points and numbered lists
    if (line.match(/^\s*[-*+]\s+/) || line.match(/^\s*\d+\.\s+/)) {
      if (!inList) {
        inList = true;
        currentList = [];
      }
      const content = line
        .replace(/^\s*[-*+]\s+/, "")
        .replace(/^\s*\d+\.\s+/, "");
      currentList.push(
        <li key={`${index}-item`} className="ml-4">
          {processInlineMarkdown(content)}
        </li>,
      );
      return null;
    } else {
      if (inList) {
        elements.push(
          <ul key={`${index}-list`} className="list-disc list-inside mb-2">
            {currentList}
          </ul>,
        );
        currentList = [];
        inList = false;
      }

      if (line.trim() === "") {
        return <br key={index} />;
      }

      return (
        <p key={index} className="mb-2">
          {processInlineMarkdown(line)}
        </p>
      );
    }
  };

  const processInlineMarkdown = (text: string) => {
    // Handle bold text **text**
    let result = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    // Handle italic text *text*
    result = result.replace(/\*(.*?)\*/g, "<em>$1</em>");

    // Handle inline code `code`
    result = result.replace(
      /`(.*?)`/g,
      '<code class="bg-secondary px-1 py-0.5 rounded text-sm">$1</code>',
    );

    // Convert HTML tags to JSX
    return (
      <span
        dangerouslySetInnerHTML={{ __html: result }}
        className="whitespace-pre-wrap"
      />
    );
  };

  lines.forEach((line, index) => {
    const element = processLine(line, index);
    if (element) {
      elements.push(element);
    }
  });

  // Handle any remaining list
  if (inList && currentList.length > 0) {
    elements.push(
      <ul key="final-list" className="list-disc list-inside mb-2">
        {currentList}
      </ul>,
    );
  }

  return elements;
};

// AI Analysis Dialog Component
const AIAnalysisDialog = ({
  open,
  onOpenChange,
  analysis,
  creatorName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysis: { score: number; notes: string } | null;
  creatorName: string;
}) => {
  if (!analysis) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            AI Analysis for {creatorName} (Score: {analysis.score}/100)
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-secondary/50">
              <h4 className="font-semibold mb-2">Overall Score</h4>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold">{analysis.score}/100</div>
                <div className="flex-1 bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${analysis.score}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2">Detailed Analysis</h4>
              <div className="prose prose-sm max-w-none text-primary/80 space-y-2">
                {renderMarkdown(analysis.notes)}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Extracted table components
const ContractsTable = ({
  contracts,
  contractsStatus,
  loadMoreContracts,
  selectedContractStatuses,
  onCreatorClick,
}: {
  contracts: (Doc<"creatorContracts"> & {
    creator: Doc<"creators"> & {
      name: string;
    };
  })[];
  contractsStatus: string;
  loadMoreContracts: (count: number) => void;
  selectedContractStatuses: string[];
  onCreatorClick: (creatorId: Id<"creators">) => void;
}) => {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">
        {!contracts || contracts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-lg text-primary/60">
              {selectedContractStatuses.length === 0
                ? "No contract status selected"
                : "No contracts found"}
            </p>
            <p className="text-sm text-primary/60">
              {selectedContractStatuses.length === 0
                ? "Please select at least one contract status to view partnerships"
                : "No partnerships match the selected status filters"}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-border bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  Creator Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {contractsStatus === "LoadingMore" ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center">
                    <Loader2 className="animate-spin" />
                  </td>
                </tr>
              ) : (
                contracts.map((partnership) => (
                  <tr
                    key={partnership.creatorId}
                    className={
                      partnership.creator._id
                        ? "cursor-pointer hover:bg-secondary/50"
                        : ""
                    }
                    onClick={() => {
                      if (partnership.creator._id) {
                        onCreatorClick(partnership.creatorId);
                      }
                    }}
                  >
                    <td className="px-6 py-4 text-sm text-primary hover:underline">
                      {partnership.creator?.name ?? "Deleted Creator"}
                    </td>
                    <td className="px-6 py-4 text-sm text-primary">
                      {formatDate(
                        new Date(partnership.startDate).toISOString(),
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-primary">
                      {formatDate(
                        new Date(
                          (partnership as Doc<"creatorContracts">).endDate,
                        ).toISOString(),
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <StatusBadge status={partnership.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {contracts &&
        contracts.length > 0 &&
        contractsStatus === "CanLoadMore" && (
          <div className="flex justify-center p-4 border-t border-border">
            <Button variant="outline" onClick={() => loadMoreContracts(10)}>
              Load More
            </Button>
          </div>
        )}
    </div>
  );
};

const ApplicationsTable = ({
  applications,
  applicationsStatus,
  loadMoreApplications,
  selectedApplicationStatuses,
  onCreatorClick,
}: {
  applications: (Doc<"creatorApplications"> & {
    creator: Doc<"creators"> & {
      name: string;
    };
  })[];
  applicationsStatus: string;
  loadMoreApplications: (count: number) => void;
  selectedApplicationStatuses: string[];
  onCreatorClick: (creatorId: Id<"creators">) => void;
}) => {
  const [selectedAnalysis, setSelectedAnalysis] = useState<{
    analysis: { score: number; notes: string };
    creatorName: string;
  } | null>(null);

  // Sort applications by AI analysis score (highest to lowest)
  const sortedApplications = [...applications].sort((a, b) => {
    const scoreA = a.aiAnalysis?.score ?? -1;
    const scoreB = b.aiAnalysis?.score ?? -1;
    return scoreB - scoreA;
  });

  const handleScoreClick = (
    e: React.MouseEvent,
    analysis: { score: number; notes: string },
    creatorName: string,
  ) => {
    e.stopPropagation();
    setSelectedAnalysis({ analysis, creatorName });
  };

  return (
    <>
      <div className="rounded-lg border border-border bg-card">
        <div className="overflow-x-auto">
          {!applications || applications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="mb-4 text-lg text-primary/60">
                {selectedApplicationStatuses.length === 0
                  ? "No application status selected"
                  : "No applications found"}
              </p>
              <p className="text-sm text-primary/60">
                {selectedApplicationStatuses.length === 0
                  ? "Please select at least one application status to view applications"
                  : "No applications match the selected status filters"}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-border bg-secondary">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    Creator Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    Apply Date
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    AI Score
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {applicationsStatus === "LoadingMore" ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center">
                      <Loader2 className="animate-spin" />
                    </td>
                  </tr>
                ) : (
                  sortedApplications.map((partnership) => (
                    <tr
                      key={partnership.creatorId}
                      className={
                        partnership.creator.name
                          ? "cursor-pointer hover:bg-secondary/50"
                          : ""
                      }
                      onClick={() => {
                        if (partnership.creator.name) {
                          onCreatorClick(partnership.creatorId);
                        }
                      }}
                    >
                      <td className="px-6 py-4 text-sm text-primary hover:underline">
                        {partnership.creator?.name ?? "Deleted Creator"}
                      </td>
                      <td className="px-6 py-4 text-sm text-primary">
                        {formatDate(
                          new Date(partnership._creationTime).toISOString(),
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {partnership.aiAnalysis ? (
                          <button
                            onClick={(e) => {
                              if (partnership.aiAnalysis) {
                                handleScoreClick(
                                  e,
                                  partnership.aiAnalysis,
                                  partnership.creator?.name ??
                                    "Unknown Creator",
                                );
                              }
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border border-primary/20 hover:bg-primary/5 transition-colors"
                          >
                            <div className="flex items-center gap-1">
                              <span className="font-bold">
                                {partnership.aiAnalysis.score}
                              </span>
                              <span className="text-primary/60">/100</span>
                            </div>
                            <div
                              className={`w-2 h-2 rounded-full ${
                                partnership.aiAnalysis.score >= 80
                                  ? "bg-green-500"
                                  : partnership.aiAnalysis.score >= 60
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                              }`}
                            />
                          </button>
                        ) : (
                          <span className="text-primary/60 text-sm">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <StatusBadge status={partnership.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        {applications &&
          applications.length > 0 &&
          applicationsStatus === "CanLoadMore" && (
            <div className="flex justify-center p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => loadMoreApplications(10)}
              >
                Load More
              </Button>
            </div>
          )}
      </div>

      <AIAnalysisDialog
        open={!!selectedAnalysis}
        onOpenChange={() => setSelectedAnalysis(null)}
        analysis={selectedAnalysis?.analysis ?? null}
        creatorName={selectedAnalysis?.creatorName ?? ""}
      />
    </>
  );
};

interface AppsPartnershipsProps {
  campaignId?: string;
}

export default function AppsPartnerships({
  campaignId: urlCampaignId,
}: AppsPartnershipsProps) {
  const { appId } = useAccount();
  const [showFindCreatorsDialog, setShowFindCreatorsDialog] = useState(false);
  const [showInviteCreatorDialog, setShowInviteCreatorDialog] = useState(false);
  const { toast } = useToast();

  // Define the status types
  type ContractStatus =
    | "pendingApproval"
    | "active"
    | "completed"
    | "terminated";
  type ApplicationStatus = "pending" | "approved" | "rejected";

  // State for status filters - separate for contracts and applications
  const [selectedContractStatuses, setSelectedContractStatuses] = useState<
    string[]
  >(["active"]);
  const [selectedApplicationStatuses, setSelectedApplicationStatuses] =
    useState<string[]>(["pending"]);
  const [hasInitialized, setHasInitialized] = useState(false);

  const {
    results: campaigns,
    status: campaignsStatus,
    loadMore: loadMoreCampaigns,
    isLoading: isLoadingCampaigns,
  } = usePaginatedQuery(
    api.campaigns.getCampaigns,
    { appId: appId as Id<"apps"> },
    { initialNumItems: 10 },
  );

  const [selectedCampaignId, setSelectedCampaignId] = useState<
    Id<"campaigns"> | undefined
  >(urlCampaignId as Id<"campaigns"> | undefined);

  const navigate = useNavigate();

  // Update selectedCampaignId when campaigns load or URL changes
  useEffect(() => {
    if (urlCampaignId) {
      // If there's a campaignId in the URL, use it
      setSelectedCampaignId(urlCampaignId as Id<"campaigns">);
    } else if (campaigns && campaigns.length > 0 && !selectedCampaignId) {
      // Otherwise, default to the first campaign
      setSelectedCampaignId(campaigns[0]._id);
      // Update URL with the default campaign
      navigate({
        to: ".",
        search: { campaignId: campaigns[0]._id },
        replace: true,
      });
    }
  }, [campaigns, selectedCampaignId, urlCampaignId, navigate]);

  const handleCampaignChange = (campaignId: Id<"campaigns">) => {
    setSelectedCampaignId(campaignId);
    // Update URL with the new campaign
    navigate({
      to: ".",
      search: { campaignId },
      replace: true,
    });
  };

  const {
    results: contracts,
    status: contractsStatus,
    loadMore: loadMoreContracts,
  } = usePaginatedQuery(
    api.partnerships.getPaginatedCreatorPartnershipsAndApplicationsForApp,
    { campaignId: selectedCampaignId, type: "contracts" },
    { initialNumItems: 10 },
  ) as {
    results:
      | (Doc<"creatorContracts"> & {
          creator: Doc<"creators"> & {
            name: string;
          };
        })[]
      | undefined;
    status: string;
    loadMore: (count: number) => void;
  };

  const {
    results: applications,
    status: applicationsStatus,
    loadMore: loadMoreApplications,
  } = usePaginatedQuery(
    api.partnerships.getPaginatedCreatorPartnershipsAndApplicationsForApp,
    { campaignId: selectedCampaignId, type: "applications" },
    { initialNumItems: 10 },
  ) as {
    results:
      | (Doc<"creatorApplications"> & {
          creator: Doc<"creators"> & {
            name: string;
          };
        })[]
      | undefined;
    status: string;
    loadMore: (count: number) => void;
  };

  // Get all unique statuses for both contracts and applications
  const allContractStatuses: ContractStatus[] = Array.from(
    new Set(
      contracts
        ?.map((contract) => contract.status)
        .filter((status): status is ContractStatus => status !== undefined) ||
        [],
    ),
  );

  const allApplicationStatuses: ApplicationStatus[] = Array.from(
    new Set(
      applications
        ?.map((application) => application.status)
        .filter(
          (status): status is ApplicationStatus => status !== undefined,
        ) || [],
    ),
  );

  // Initialize selectedStatuses when data loads (only once)
  useEffect(() => {
    if (
      (allContractStatuses.length > 0 || allApplicationStatuses.length > 0) &&
      !hasInitialized
    ) {
      // Smart defaults based on what statuses actually exist in the data

      // For contracts: prefer "active" if it exists, otherwise show all available statuses
      if (allContractStatuses.length > 0) {
        const hasActiveContracts = allContractStatuses.includes("active");
        setSelectedContractStatuses(
          hasActiveContracts ? ["active"] : [...allContractStatuses],
        );
      }

      // For applications: prefer "pending" if it exists, otherwise show all available statuses
      if (allApplicationStatuses.length > 0) {
        const hasPendingApplications =
          allApplicationStatuses.includes("pending");
        setSelectedApplicationStatuses(
          hasPendingApplications ? ["pending"] : [...allApplicationStatuses],
        );
      }

      setHasInitialized(true);
    }
  }, [
    allContractStatuses.length,
    allApplicationStatuses.length,
    hasInitialized,
    allContractStatuses,
    allApplicationStatuses,
  ]);

  // Filter contracts and applications based on selected statuses
  const filteredContracts =
    contracts?.filter(
      (contract) =>
        contract.status && selectedContractStatuses.includes(contract.status),
    ) || [];

  const filteredApplications =
    applications?.filter(
      (application) =>
        application.status &&
        selectedApplicationStatuses.includes(application.status),
    ) || [];

  const handleContractStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedContractStatuses((prev) => [...prev, status]);
    } else {
      setSelectedContractStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleApplicationStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedApplicationStatuses((prev) => [...prev, status]);
    } else {
      setSelectedApplicationStatuses((prev) =>
        prev.filter((s) => s !== status),
      );
    }
  };

  const handleSelectAllContracts = () => {
    setSelectedContractStatuses([...allContractStatuses]);
  };

  const handleSelectNoneContracts = () => {
    setSelectedContractStatuses([]);
  };

  const handleSelectAllApplications = () => {
    setSelectedApplicationStatuses([...allApplicationStatuses]);
  };

  const handleSelectNoneApplications = () => {
    setSelectedApplicationStatuses([]);
  };

  const handleApplicationClick = (creatorId: Id<"creators">) => {
    navigate({
      to: CreatorProfilePage.fullPath,
      params: { creatorId },
    });
  };

  const handleShareApplication = async () => {
    const url = `${window.location.origin}/campaigns?campaignId=${selectedCampaignId}`;
    await navigator.clipboard.writeText(url);
    toast({
      title: "Link copied",
      description:
        "Share this link with creators so they can apply for a partnership with you.",
    });
  };

  if (isLoadingCampaigns) {
    return (
      <div className="absolute inset-0 flex h-full w-full items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  //TODO: handle campaigns that have ended
  if (campaigns?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-lg text-primary/60">
          No campaigns found for this app. Please create a campaign first on the
          dashboard.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate({ to: "/dashboard" })}
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  const hasContracts = filteredContracts && filteredContracts.length > 0;
  const hasApplications =
    filteredApplications && filteredApplications.length > 0;
  const hasNoApplicationsOrContracts = !hasContracts && !hasApplications;

  // Get selected campaign for end date validation
  const selectedCampaign = campaigns?.find(
    (campaign) => campaign._id === selectedCampaignId,
  );

  const totalSelectedStatuses =
    selectedContractStatuses.length + selectedApplicationStatuses.length;
  const totalAvailableStatuses =
    allContractStatuses.length + allApplicationStatuses.length;

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-4">
        {/* Campaign Selector */}
        {campaigns && campaigns.length > 0 && (
          <CampaignSelector
            campaigns={campaigns}
            selectedCampaignId={selectedCampaignId}
            onCampaignChange={handleCampaignChange}
            campaignsStatus={campaignsStatus}
            loadMoreCampaigns={loadMoreCampaigns}
          />
        )}

        <div className="flex items-center justify-between">
          {!hasNoApplicationsOrContracts && (
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowInviteCreatorDialog(true)}
              >
                <Mail className="w-4 h-4 mr-2" />
                Invite Creator
              </Button>
              <Button variant="outline" onClick={handleShareApplication}>
                <Send className="w-4 h-4 mr-2" />
                Share Application Link
              </Button>
            </div>
          )}

          <div className="flex items-center gap-4 ml-auto">
            {/* Status Filter */}
            {totalAvailableStatuses > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Filter className="h-4 w-4 mr-2" />
                    Filter ({totalSelectedStatuses}/{totalAvailableStatuses})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="left" className="w-64">
                  <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  {/* Contract Statuses Section */}
                  {allContractStatuses.length > 0 && (
                    <>
                      <DropdownMenuLabel className="text-xs font-medium text-primary/80">
                        Contract Statuses
                      </DropdownMenuLabel>

                      <div className="flex items-center justify-between px-2 py-1">
                        <Button
                          onClick={handleSelectAllContracts}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          All
                        </Button>
                        <Button
                          onClick={handleSelectNoneContracts}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          None
                        </Button>
                      </div>

                      {allContractStatuses.map((status) => (
                        <DropdownMenuCheckboxItem
                          key={`contract-${status}`}
                          checked={selectedContractStatuses.includes(status)}
                          onCheckedChange={(checked) =>
                            handleContractStatusToggle(status, checked)
                          }
                        >
                          <div className="flex items-center space-x-2">
                            <StatusBadge status={status} />
                            <span className="sr-only">
                              {status.replace("_", " ")}
                            </span>
                          </div>
                        </DropdownMenuCheckboxItem>
                      ))}

                      {allApplicationStatuses.length > 0 && (
                        <DropdownMenuSeparator />
                      )}
                    </>
                  )}

                  {/* Application Statuses Section */}
                  {allApplicationStatuses.length > 0 && (
                    <>
                      <DropdownMenuLabel className="text-xs font-medium text-primary/80">
                        Application Statuses
                      </DropdownMenuLabel>

                      <div className="flex items-center justify-between px-2 py-1">
                        <Button
                          onClick={handleSelectAllApplications}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          All
                        </Button>
                        <Button
                          onClick={handleSelectNoneApplications}
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                        >
                          None
                        </Button>
                      </div>

                      {allApplicationStatuses.map((status) => (
                        <DropdownMenuCheckboxItem
                          key={`application-${status}`}
                          checked={selectedApplicationStatuses.includes(status)}
                          onCheckedChange={(checked) =>
                            handleApplicationStatusToggle(status, checked)
                          }
                        >
                          <div className="flex items-center space-x-2">
                            <StatusBadge status={status} />
                            <span className="sr-only">
                              {status.replace("_", " ")}
                            </span>
                          </div>
                        </DropdownMenuCheckboxItem>
                      ))}
                    </>
                  )}

                  <DropdownMenuSeparator />

                  <div className="px-2 py-1 text-xs text-muted-foreground">
                    Contracts: {filteredContracts.length} of{" "}
                    {contracts?.length || 0}
                    <br />
                    Applications: {filteredApplications.length} of{" "}
                    {applications?.length || 0}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {hasNoApplicationsOrContracts && (
          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-primary mb-2">
                  No Partnerships Yet
                </h3>
                <p className="text-primary/60 mb-4">
                  Applications and future contracts will appear here once
                  creators start applying to your campaign or you establish
                  partnerships with them.
                </p>
                <p className="text-primary/60 mb-6">
                  For now, you can invite your own existing creators or share
                  the application link with your network.
                </p>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <Button
                    variant="secondary"
                    onClick={() => setShowInviteCreatorDialog(true)}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Invite Creator
                  </Button>
                  <Button variant="outline" onClick={handleShareApplication}>
                    <Send className="w-4 h-4 mr-2" />
                    Share Application Link
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <>
          <Dialog
            open={showFindCreatorsDialog}
            onOpenChange={setShowFindCreatorsDialog}
          >
            <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Find Creators</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto min-h-0">
                <CreatorsList
                  campaignId={selectedCampaignId}
                  onCreatorClick={handleApplicationClick}
                />
              </div>
            </DialogContent>
          </Dialog>

          <InviteCreatorDialog
            open={showInviteCreatorDialog}
            onOpenChange={setShowInviteCreatorDialog}
            campaignId={selectedCampaignId as Id<"campaigns">}
            maxEndDate={selectedCampaign?.endDate}
          />

          {hasContracts && hasApplications ? (
            <Tabs defaultValue="contracts" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="contracts">Active Creators</TabsTrigger>
                <TabsTrigger value="applications">
                  Applications
                  {filteredApplications?.filter(
                    (app) => app.status === "pending",
                  ).length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-primary/10 rounded-full">
                      {
                        filteredApplications.filter(
                          (app) => app.status === "pending",
                        ).length
                      }
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="contracts" className="mt-0">
                <ContractsTable
                  contracts={filteredContracts}
                  contractsStatus={contractsStatus}
                  loadMoreContracts={loadMoreContracts}
                  selectedContractStatuses={selectedContractStatuses}
                  onCreatorClick={handleApplicationClick}
                />
              </TabsContent>

              <TabsContent value="applications" className="mt-0">
                <ApplicationsTable
                  applications={filteredApplications}
                  applicationsStatus={applicationsStatus}
                  loadMoreApplications={loadMoreApplications}
                  selectedApplicationStatuses={selectedApplicationStatuses}
                  onCreatorClick={handleApplicationClick}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="w-full">
              {hasContracts && (
                <ContractsTable
                  contracts={filteredContracts}
                  contractsStatus={contractsStatus}
                  loadMoreContracts={loadMoreContracts}
                  selectedContractStatuses={selectedContractStatuses}
                  onCreatorClick={handleApplicationClick}
                />
              )}
              {hasApplications && (
                <ApplicationsTable
                  applications={filteredApplications}
                  applicationsStatus={applicationsStatus}
                  loadMoreApplications={loadMoreApplications}
                  selectedApplicationStatuses={selectedApplicationStatuses}
                  onCreatorClick={handleApplicationClick}
                />
              )}
            </div>
          )}
        </>
      </div>
    </div>
  );
}
